import { useEffect } from 'react';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import { useDispatch, useSelector } from 'react-redux';
import { useApiReady, usePreloadImage } from '../../../shared/hooks';
import { CHANNEL } from '../constants/messageTemplateKeys';
import {
  loadDisclosures,
  loadTemplateCampaigns,
  loadTemplateTags,
  loadTemplateTenants,
  loadTemplateTriggers,
  loadTemplateVariables,
  selectTemplatesCampaignsStatus,
  selectTemplatesTagsStatus,
  selectTemplatesTriggersStatus,
  selectTemplatesVariablesStatus,
  selectTemplatesTenantSettingsStatus,
  selectTemplatesTenantLogo,
  selectTemplatesTenantSender,
  selectTemplatesDisclosuresStatus,
  selectTemplatesDisclosures,
  selectTemplatesTenantsStatus,
} from '../store/templatesSlice';
import { selectIsAdmin } from '../../auth/store/authSlice';
import { templatesTriggersReset, templatesVariablesReset } from '../store/templatesMetaThunks';
import { getCreateTemplateInitialLoadingChecks } from '../lib/getCreateTemplateInitialLoadingChecks';
import { resolveTriggerChannel } from '../lib/resolveTriggerChannel';
import { useTenantSettingsLoader } from './useTenantSettingsLoader';

export function useCreateTemplateMetaLoader({ channel } = {}) {
  const effectiveChannel = channel === CHANNEL.MMS ? CHANNEL.SMS : channel;
  const triggerChannel = resolveTriggerChannel(channel);
  const dispatch = useDispatch();
  const apiReady = useApiReady();
  useTenantSettingsLoader();

  const campaignsStatus = useSelector(selectTemplatesCampaignsStatus);
  const tagsStatus = useSelector(selectTemplatesTagsStatus);
  const triggersStatus = useSelector(selectTemplatesTriggersStatus);
  const variablesStatus = useSelector(selectTemplatesVariablesStatus);
  const tenantSettingsStatus = useSelector(selectTemplatesTenantSettingsStatus);
  const disclosuresStatus = useSelector(selectTemplatesDisclosuresStatus);
  const tenantsStatus = useSelector(selectTemplatesTenantsStatus);
  const isAdmin = useSelector(selectIsAdmin);
  const tenantLogoUrl = useSelector(selectTemplatesTenantLogo);
  const tenantSender = useSelector(selectTemplatesTenantSender);
  const disclosures = useSelector(selectTemplatesDisclosures);

  useEffect(() => {
    const {
      shouldLoadCampaigns,
      shouldLoadTags,
      shouldLoadTriggers,
      shouldLoadVariables,
      shouldLoadDisclosures,
    } = getCreateTemplateInitialLoadingChecks({
      apiReady,
      campaignsStatus,
      tagsStatus,
      triggersStatus,
      variablesStatus,
      disclosuresStatus,
    });

    if (shouldLoadCampaigns) dispatch(loadTemplateCampaigns());
    if (shouldLoadTags) dispatch(loadTemplateTags());
    if (shouldLoadTriggers) dispatch(loadTemplateTriggers({ channel: triggerChannel }));
    if (shouldLoadVariables) dispatch(loadTemplateVariables({ channel: effectiveChannel }));
    if (shouldLoadDisclosures) dispatch(loadDisclosures());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads are gated by status, not channel
  }, [
    dispatch,
    apiReady,
    campaignsStatus,
    tagsStatus,
    triggersStatus,
    variablesStatus,
    disclosuresStatus,
  ]);

  // Tenant list backs the admin-only "Select tenant" multiselect. Loaded only for admins
  // and kept out of isMetaLoading so it never blocks the page for regular users.
  useEffect(() => {
    if (apiReady && isAdmin && tenantsStatus === LOADING_STATUS.IDLE) {
      dispatch(loadTemplateTenants());
    }
  }, [dispatch, apiReady, isAdmin, tenantsStatus]);

  useEffect(() => {
    return () => {
      dispatch(templatesVariablesReset());
      dispatch(templatesTriggersReset());
    };
  }, [dispatch]);

  usePreloadImage(tenantLogoUrl);

  const isMetaLoading =
    !apiReady ||
    [
      campaignsStatus,
      tagsStatus,
      triggersStatus,
      variablesStatus,
      tenantSettingsStatus,
      disclosuresStatus,
    ].some((s) => s === LOADING_STATUS.IDLE || s === LOADING_STATUS.LOADING);

  return {
    disclosures,
    tenantLogoUrl,
    tenantSender,
    isMetaLoading,
    variableChannel: effectiveChannel,
  };
}
